import { EventLocation, EventType } from '../../types';
import { apiService } from './apiService';

export const eventsService = {
    // Event Locations
    getEventLocations: async (params?: { page?: number; pagination?: boolean; workplace?: number }): Promise<EventLocation[]> => {
        try {
            const workplaceId = params?.workplace ?? Number(localStorage.getItem("workplace-id"));
            const queryParams = {
                page: params?.page ?? 1,
                pagination: params?.pagination ?? false,
                ...(workplaceId && { workplace: workplaceId }),
            };

            const response = await apiService.get('settings_locations', { params: queryParams });
            const data = apiService.responseHandler(response);
            // Handle both array and { member: [...] } formats
            const locations = Array.isArray(data) ? data : (data?.member || []);
            return locations.map((item: any) => ({
                id: item.id?.toString() || '',
                label: item.label || '',
                name: item.label || item.name || '', // Keep name for backward compatibility
                active: item.active ?? true,
                workplace: item.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
            }));
        } catch (error) {
            console.error('Failed to fetch event locations:', error);
            return [];
        }
    },

    addEventLocation: async (location: Omit<EventLocation, 'id'>): Promise<EventLocation> => {
        const workplaceId = Number(localStorage.getItem("workplace-id"));
        const payload = {
            label: location.label || location.name || '',
            active: true, // Always pass true
            workplace: location.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.post('settings_locations', payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || Math.random().toString(),
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    updateEventLocation: async (location: EventLocation): Promise<EventLocation> => {
        const workplaceId = Number(localStorage.getItem("workplace-id"));
        const payload = {
            label: location.label || location.name || '',
            active: true, // Always pass true
            workplace: location.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.put(`settings_locations/${location.id}`, payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || location.id,
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    deleteEventLocation: async (id: string): Promise<boolean> => {
        try {
            await apiService.delete(`settings_locations/${id}`);
            return true;
        } catch (error) {
            console.error('Failed to delete event location:', error);
            return false;
        }
    },

    // Event Types
    getEventTypes: async (params?: { page?: number; itemsPerPage?: number; pagination?: boolean; workplace?: number }): Promise<EventType[]> => {
        try {
            const workplaceId = params?.workplace ?? Number(localStorage.getItem("workplace-id"));
            const queryParams = {
                page: params?.page ?? 1,
                itemsPerPage: params?.itemsPerPage ?? 30,
                pagination: params?.pagination ?? false,
                ...(workplaceId && { workplace: workplaceId }),
            };

            const response = await apiService.get('settings_event_types', { params: queryParams });
            const data = apiService.responseHandler(response);
            // Handle both array and { member: [...] } formats
            const types = Array.isArray(data) ? data : (data?.member || []);
            return types.map((item: any) => ({
                id: item.id?.toString() || '',
                label: item.label || '',
                name: item.label || item.name || '', // Keep name for backward compatibility
                active: item.active ?? true,
                workplace: item.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
            }));
        } catch (error) {
            console.error('Failed to fetch event types:', error);
            return [];
        }
    },

    addEventType: async (type: Omit<EventType, 'id'>): Promise<EventType> => {
        const workplaceId = Number(localStorage.getItem("workplace-id"));
        const payload = {
            label: type.label || type.name || '',
            active: type.active ?? true,
            workplace: type.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.post('settings_event_types', payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || Math.random().toString(),
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    updateEventType: async (type: EventType): Promise<EventType> => {
        const workplaceId = Number(localStorage.getItem("workplace-id"));
        const payload = {
            label: type.label || type.name || '',
            active: type.active ?? true,
            workplace: type.workplace || (workplaceId ? `/api/workplaces/${workplaceId}` : undefined),
        };

        const response = await apiService.put(`settings_event_types/${type.id}`, payload);
        const data = apiService.responseHandler(response);
        return {
            id: data.id?.toString() || type.id,
            label: data.label || payload.label,
            name: data.label || payload.label, // Keep name for backward compatibility
            active: data.active ?? true,
            workplace: data.workplace || payload.workplace,
        };
    },

    deleteEventType: async (id: string): Promise<boolean> => {
        try {
            await apiService.delete(`settings_event_types/${id}`);
            return true;
        } catch (error) {
            console.error('Failed to delete event type:', error);
            return false;
        }
    },
};

